import { Component, inject, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { CompanyService } from '../../services/company.service';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { UserService } from '../../services/user.service';
import { SubscriptionService } from '../../services/subscription.service';
import { COUNTRY_TO_CURRENCY_MAP } from '../../../shared/constants/country-currency.constant';
import { CompanyClient } from '../../../shared';
import { User } from '../../models';

@Component({
  selector: 'kt-app-companies',
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './companies.component.html',
  styleUrl: './companies.component.scss'
})
export class CompaniesComponent implements OnInit {
  private companyService = inject(CompanyService);
  private userService = inject(UserService);

  companies = signal<CompanyClient[]>([]);
  currentUser: User | null = null;
  companyForm!: FormGroup;
  fb = inject(FormBuilder);
  showAddCompanyForm = false;
  editingCompanyId: string | null = null;
  private subscriptionService = inject(SubscriptionService);

  defaultCountry = 'Canada';

  // Country to Currency mapping
    countryToCurrencyMap = COUNTRY_TO_CURRENCY_MAP;

  /** Whether the user can add another company (subscription + limit check). */
  get canAddCompany(): boolean {
    return this.subscriptionService.canAddCompany(this.companies().length);
  }

  ngOnInit(): void {
    this.currentUser = this.userService.currentUser();

    this.fetchCompanies();
    this.initializeForm();
  }

  private isValidUserId(userId: string): boolean {
    return Boolean(userId?.trim());
  }

  private fetchCompanies() {
    if (!this.currentUser || !this.isValidUserId(this.currentUser.id)) {
      this.companies.set([]);
      return;
    }

    this.companyService.getCompanies(this.currentUser.id).subscribe({
      next: (companies) => {
        this.companies.set(companies);
      },
      error: (error) => {
        console.error('Error fetching companies:', error);
        this.companies.set([]);
      }
    });
  }

  initializeForm(): void {
    this.companyForm = this.fb.group({
      companyName: ['', Validators.required],
      companyEmail: ['', [Validators.required, Validators.email]],
      companyPhone: [''],
      companyAddress: ['', Validators.required],
      companyCity: ['', Validators.required],
      companyProvince: ['', Validators.required],
      companyPostalCode: ['', Validators.required],
      companyCountry: ['Canada', Validators.required],
      companyWebsite: [''],
      companyGst: [''],
      companyHst: [''],
      isPrimary: [false]
    });
  }

  toggleAddCompanyForm(): void {
    this.showAddCompanyForm = !this.showAddCompanyForm;
    if (!this.showAddCompanyForm) {
      this.editingCompanyId = null;
      this.companyForm.reset({ companyCountry: this.defaultCountry });
    }
  }

  onPremiumAddCompanyClick(): void {
    this.subscriptionService.checkAndShowPremium('company', this.companies().length);
  }

  editCompany(company: CompanyClient): void {
    if(company.id) {
      this.editingCompanyId = company.id;
    } else {
      console.warn('Attempting to edit a company without a valid ID.');
      return;
    }

    this.companyForm.patchValue({
      companyName: company.name,
      companyEmail: company.email,
      companyPhone: company.phone ?? '',
      companyAddress: company.address?.address ?? '',
      companyCity: company.address?.city ?? '',
      companyProvince: company.address?.province ?? '',
      companyPostalCode: company.address?.postalCode ?? '',
      companyCountry: company.address?.country ?? this.defaultCountry,
      companyWebsite: company.websiteLink ?? '',
      companyGst: company.gstNumber ?? '',
      companyHst: company.hstNumber ?? '',
      isPrimary: company.isPrimary ?? false
    });
    this.showAddCompanyForm = true;
  }

  onSubmit(): void {
    if (this.companyForm.invalid) {
      this.companyForm.markAllAsTouched();
      return;
    }
    if (this.editingCompanyId) {
      this.updateCompany();
    } else {
      this.addCompany();
    }
  }

  private addCompany() {
    const companyDetails: CompanyClient = {
      id: '',
      userId: this.currentUser!.id,
      name: this.companyForm.value.companyName,
      phone: this.companyForm.value.companyPhone,
      email: this.companyForm.value.companyEmail,
      address: {
        address: this.companyForm.value.companyAddress,
        city: this.companyForm.value.companyCity,
        province: this.companyForm.value.companyProvince,
        postalCode: this.companyForm.value.companyPostalCode,
        country: this.companyForm.value.companyCountry,
        countryCurrency: this.countryToCurrencyMap[this.companyForm.value.companyCountry] || 'CAD'
      },
      websiteLink: this.companyForm.value.companyWebsite ?? '',
      taxEnabled: true,
      gstNumber: this.companyForm.value.companyGst ?? '',
      hstNumber: this.companyForm.value.companyHst ?? '',
      isPrimary: this.companyForm.value.isPrimary ?? false
    };

    this.companyService.addCompanyDetails(companyDetails).subscribe({
      next: () => {
        this.companyForm.reset({ companyCountry: this.defaultCountry });
        this.showAddCompanyForm = false;
        this.fetchCompanies();
      },
      error: (error) => {
        console.error('Error adding company:', error);
      }
    });
  }

  private updateCompany(): void {
    if (!this.editingCompanyId || !this.currentUser) return;
    const companyDetails: CompanyClient = {
      id: this.editingCompanyId,
      userId: this.currentUser.id,
      name: this.companyForm.value.companyName,
      phone: this.companyForm.value.companyPhone,
      email: this.companyForm.value.companyEmail,
      address: {
        address: this.companyForm.value.companyAddress,
        city: this.companyForm.value.companyCity,
        province: this.companyForm.value.companyProvince,
        postalCode: this.companyForm.value.companyPostalCode,
        country: this.companyForm.value.companyCountry,
        countryCurrency: this.countryToCurrencyMap[this.companyForm.value.companyCountry] || 'CAD'
      },
      websiteLink: this.companyForm.value.companyWebsite ?? '',
      taxEnabled: true,
      gstNumber: this.companyForm.value.companyGst ?? '',
      hstNumber: this.companyForm.value.companyHst ?? '',
      isPrimary: this.companyForm.value.isPrimary ?? false
    };
    this.companyService.updateCompanyDetails(this.editingCompanyId, companyDetails).subscribe({
      next: () => {
        this.companyForm.reset({ companyCountry: this.defaultCountry });
        this.editingCompanyId = null;
        this.showAddCompanyForm = false;
        this.fetchCompanies();
      },
      error: (error) => {
        console.error('Error updating company:', error);
      }
    });
  }
}
